import React, { useState } from 'react';
import { MdClose } from 'react-icons/md';
import { BookingData, Coupon } from '../../@types';
import CouponService from '../../services/CouponService';

interface GetCouponDetailsProps {
  onCouponFound: (coupon: Coupon) => void;
  onCouponRemoved?: (couponId: number) => void;
  appliedCoupons?: Coupon[];
  bookingData?: BookingData;
  paymentType?: 'skating' | 'hockey' | 'lessons' | 'passes' | 'parties';
}

const GetCouponDetails: React.FC<GetCouponDetailsProps> = ({
  onCouponFound,
  onCouponRemoved,
  appliedCoupons = [],
  bookingData,
  paymentType,
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValidCoupon, setIsValidCoupon] = useState<boolean | null>(null);

  /**
   * Get day of week code from date string (M, T, W, TH, F, S, SU)
   */
  const getDayOfWeekCode = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const dayMap: Record<number, string> = {
      0: 'SU',
      1: 'M',
      2: 'T',
      3: 'W',
      4: 'TH',
      5: 'F',
      6: 'S',
    };
    return dayMap[day] || '';
  };

  /**
   * Convert time slot string (e.g., "10:00 AM" or "10:00") to numeric format (e.g., 1000)
   */
  const parseTimeSlotToNumber = (timeSlot: string): number | null => {
    if (!timeSlot) return null;

    // Handle formats like "10:00 AM", "10:00 PM", or "10:00"
    let timeStr = timeSlot.trim().toUpperCase();

    // Remove AM/PM and extract time
    const isPM = timeStr.includes('PM');
    const isAM = timeStr.includes('AM');
    timeStr = timeStr.replace(/\s*(AM|PM)\s*/i, '');

    const [hoursStr, minutesStr] = timeStr.split(':');
    if (!hoursStr || !minutesStr) return null;

    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (isNaN(hours) || isNaN(minutes)) return null;

    // Convert 12-hour to 24-hour format
    if (isPM && hours !== 12) {
      hours += 12;
    } else if (isAM && hours === 12) {
      hours = 0;
    }

    return hours * 100 + minutes;
  };

  const parseDateOnly = (
    dateString: string | null | undefined,
  ): Date | null => {
    if (!dateString) return null;
    const datePart = dateString.split('T')[0];
    if (!datePart) return null;

    const [yearStr, monthStr, dayStr] = datePart.split('-');
    if (!yearStr || !monthStr || !dayStr) return null;

    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);

    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }

    return new Date(Date.UTC(year, month - 1, day));
  };

  const formatDateForDisplay = (
    dateString: string | null | undefined,
  ): string => {
    const parsed = parseDateOnly(dateString);
    if (!parsed) {
      return dateString || '';
    }

    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  /**
   * Validate coupon for current booking (applicableDays and timeRange)
   */
  const validateCouponForBooking = (coupon: Coupon): string | null => {
    // Only validate for skating payment type
    if (paymentType !== 'skating' || !bookingData) {
      return null; // No validation needed or no booking data available
    }

    const skatingData = bookingData as BookingData;
    if (!skatingData.selectedTimeSlot) {
      return null; // No time slot selected yet
    }

    const { date, timeSlot } = skatingData.selectedTimeSlot;
    const issues: string[] = [];

    // Check applicable date range
    if (coupon.validFrom || coupon.validTo) {
      const bookingDateOnly = parseDateOnly(date);
      if (bookingDateOnly) {
        const validFromDate = parseDateOnly(coupon.validFrom);
        const validToDate = parseDateOnly(coupon.validTo);

        const isBeforeValidFrom =
          validFromDate !== null &&
          bookingDateOnly.getTime() < validFromDate.getTime();
        const isAfterValidTo =
          validToDate !== null &&
          bookingDateOnly.getTime() > validToDate.getTime();

        if (isBeforeValidFrom || isAfterValidTo) {
          const bookingDateLabel = formatDateForDisplay(date);
          const validFromLabel = formatDateForDisplay(coupon.validFrom);
          const validToLabel = formatDateForDisplay(coupon.validTo);

          let message = `This coupon is not valid on ${bookingDateLabel}.`;
          if (validFromLabel && validToLabel) {
            message = `This coupon is only valid from ${validFromLabel} to ${validToLabel}. Your booking is for ${bookingDateLabel}.`;
          } else if (validFromLabel) {
            message = `This coupon is only valid on or after ${validFromLabel}. Your booking is for ${bookingDateLabel}.`;
          } else if (validToLabel) {
            message = `This coupon is only valid on or before ${validToLabel}. Your booking is for ${bookingDateLabel}.`;
          }

          issues.push(message);
        }
      }
    }

    // Check applicable days
    if (coupon.applicableDays && coupon.applicableDays !== null) {
      const dayOfWeek = getDayOfWeekCode(date);
      const applicableDays = coupon.applicableDays
        .split(',')
        .map(d => d.trim());
      if (!applicableDays.includes(dayOfWeek)) {
        const dayNames: Record<string, string> = {
          M: 'Monday',
          T: 'Tuesday',
          W: 'Wednesday',
          TH: 'Thursday',
          F: 'Friday',
          S: 'Saturday',
          SU: 'Sunday',
        };
        const applicableDayNames = applicableDays
          .map(d => dayNames[d] || d)
          .join(', ');
        issues.push(
          `This coupon is only valid on ${applicableDayNames}. Your booking is for ${dayNames[dayOfWeek] || dayOfWeek}.`,
        );
      }
    }

    // Check time range
    if (
      (coupon.timeRangeFrom !== null && coupon.timeRangeFrom !== undefined) ||
      (coupon.timeRangeTo !== null && coupon.timeRangeTo !== undefined)
    ) {
      const bookingTime = parseTimeSlotToNumber(timeSlot);
      if (bookingTime !== null) {
        const rangeFrom = coupon.timeRangeFrom || 0;
        const rangeTo = coupon.timeRangeTo || 2359;

        if (bookingTime < rangeFrom || bookingTime > rangeTo) {
          // Format time for display
          const formatTime = (timeNum: number): string => {
            const hours = Math.floor(timeNum / 100);
            const minutes = timeNum % 100;
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          };
          issues.push(
            `This coupon is only valid between ${formatTime(rangeFrom)} and ${formatTime(rangeTo)}. Your booking time is ${timeSlot}.`,
          );
        }
      }
    }

    return issues.length > 0 ? issues.join(' ') : null;
  };

  const handleApply = async () => {
    if (!couponCode.trim()) {
      setError('Please enter a coupon code');
      return;
    }

    // Check if coupon is already applied (only if appliedCoupons is provided)
    const normalizedCode = couponCode.trim().toUpperCase();
    if (
      appliedCoupons.length > 0 &&
      appliedCoupons.some(c => c.couponCode.toUpperCase() === normalizedCode)
    ) {
      setError('This coupon code has already been applied');
      setIsValidCoupon(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setIsValidCoupon(null);

    try {
      const response = (await CouponService.getCouponByCode(
        couponCode.trim(),
      )) as unknown as Coupon;

      // Check if coupon is already in the applied list (only if appliedCoupons is provided)
      if (
        appliedCoupons.length > 0 &&
        appliedCoupons.some(c => c.id === response.id)
      ) {
        setError('This coupon has already been applied');
        setIsValidCoupon(false);
        return;
      }

      // Validate coupon for current booking (show warning but allow adding)
      const validationMessage = validateCouponForBooking(response);

      onCouponFound(response);
      setIsValidCoupon(true);
      setCouponCode(''); // Clear the input after successful lookup

      // Show warning if coupon is not valid for current booking
      if (validationMessage) {
        setError(`Coupon added but may not be valid: ${validationMessage}`);
      }
    } catch (err) {
      setError('Coupon not found or invalid');
      setIsValidCoupon(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveCoupon = (couponId: number) => {
    if (onCouponRemoved) {
      onCouponRemoved(couponId);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApply();
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Enter Coupon Code
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={couponCode}
            onChange={e => setCouponCode(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter coupon code"
            className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
              isValidCoupon === true
                ? 'border-green-500 focus:ring-green-500'
                : isValidCoupon === false
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
            }`}
            disabled={isLoading}
          />
          <button
            onClick={handleApply}
            disabled={isLoading || !couponCode.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            {isLoading ? 'Loading...' : 'Apply'}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">
          {error}
        </div>
      )}

      {appliedCoupons && appliedCoupons.length > 0 && onCouponRemoved && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Applied Coupons ({appliedCoupons.length})
          </label>
          <div className="space-y-2">
            {appliedCoupons.map(coupon => {
              // Check if coupon is valid for current booking
              const validationMessage = validateCouponForBooking(coupon);
              const isValid = !validationMessage;

              return (
                <div
                  key={coupon.id}
                  className={`flex items-center justify-between border rounded-md p-3 ${
                    isValid
                      ? 'bg-green-50 border-green-200'
                      : 'bg-yellow-50 border-yellow-300'
                  }`}
                >
                  <div className="flex-1">
                    <div
                      className={`font-medium ${
                        isValid ? 'text-green-900' : 'text-yellow-900'
                      }`}
                    >
                      {coupon.couponCode}
                    </div>
                    <div
                      className={`text-sm ${
                        isValid ? 'text-green-700' : 'text-yellow-700'
                      }`}
                    >
                      {coupon.discountType === 'Percentage'
                        ? `${coupon.discountValue}% off`
                        : `$${coupon.discountValue} off`}
                    </div>
                    {!isValid && validationMessage && (
                      <div className="text-xs text-yellow-800 mt-1">
                        ⚠️ {validationMessage}
                      </div>
                    )}
                    {isValid &&
                      (coupon.applicableDays ||
                        coupon.timeRangeFrom ||
                        coupon.timeRangeTo) && (
                        <div className="text-xs text-green-600 mt-1">
                          ✓ Valid for this booking
                        </div>
                      )}
                  </div>
                  <button
                    onClick={() => handleRemoveCoupon(coupon.id)}
                    className={`ml-2 transition-colors ${
                      isValid
                        ? 'text-red-600 hover:text-red-800'
                        : 'text-yellow-700 hover:text-yellow-900'
                    }`}
                    aria-label="Remove coupon"
                  >
                    <MdClose className="w-5 h-5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default GetCouponDetails;
